// src/utils/scrollUtils.ts

interface ScrollState {
  position: number;
  timestamp: number;
}

export const scrollUtils = {
  // Save current scroll position
  saveScrollPosition: (key: string = 'user-management-scroll'): number => {
    if (typeof window !== 'undefined') {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const scrollState: ScrollState = {
        position: scrollY,
        timestamp: Date.now()
      };
      sessionStorage.setItem(key, JSON.stringify(scrollState));
      return scrollY;
    }
    return 0;
  },

  // Restore scroll position
  restoreScrollPosition: (key: string = 'user-management-scroll', delay: number = 100) => {
    if (typeof window !== 'undefined') {
      const savedScroll = sessionStorage.getItem(key);
      if (savedScroll) {
        try {
          const { position, timestamp } = JSON.parse(savedScroll);
          // Only restore if saved within last 5 seconds (prevent stale restores)
          if (Date.now() - timestamp < 5000) {
            setTimeout(() => {
              window.scrollTo({
                top: position,
                behavior: 'smooth'
              });
              // Clear after restoring
              setTimeout(() => {
                sessionStorage.removeItem(key);
              }, 500);
            }, delay);
          } else {
            sessionStorage.removeItem(key);
          }
        } catch (error) {
          console.error('Failed to restore scroll position:', error);
          sessionStorage.removeItem(key);
        }
      }
    }
  },

  // Prevent body scroll with proper handling
  preventBodyScroll: () => {
    if (typeof document !== 'undefined') {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      const scrollY = window.scrollY;
      
      // Store current scroll position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      document.body.style.overflow = 'hidden';
      
      // Store the scroll position for restoration
      document.body.dataset.scrollPosition = scrollY.toString();
    }
  },

  // Allow body scroll with proper restoration
  allowBodyScroll: () => {
    if (typeof document !== 'undefined') {
      const scrollY = document.body.dataset.scrollPosition;
      
      // Reset styles
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      document.body.style.overflow = '';
      
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
        delete document.body.dataset.scrollPosition;
      }
    }
  },

  // Get current scroll position
  getCurrentScrollPosition: (): number => {
    if (typeof window !== 'undefined') {
      return window.scrollY || document.documentElement.scrollTop;
    }
    return 0;
  },

  // Scroll to element
  scrollToElement: (elementId: string, behavior: ScrollBehavior = 'smooth') => {
    if (typeof document !== 'undefined') {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior, block: 'start' });
      }
    }
  },

  // Scroll to top
  scrollToTop: (behavior: ScrollBehavior = 'smooth') => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior });
    }
  },

  // Check if scrolled to bottom
  isScrolledToBottom: (threshold: number = 100): boolean => {
    if (typeof window !== 'undefined') {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      return scrollTop + windowHeight >= documentHeight - threshold;
    }
    return false;
  }
};